import type { ComponentProps } from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useSWR from "swr";
import type { AxiosError } from "axios";
import Axios from "axios";
import { StringParam, useQueryParams } from "use-query-params";
import {
  ButtonWithConfirmDialog,
  OutlineIconButtonMedium,
  PrimaryButtonWithPlusIcon,
} from "../../../../../components/Buttons/Buttons";
import { DeleteIcon, EditIcon } from "../../../../../components/Icons/Icons";
import { Row } from "../../../../../components/Layout/Layout";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import {
  TextXlBold,
  TextMdBold,
} from "../../../../../components/Typography/TypographyV2";
import { BorderCard } from "../../../../../layout/portalPageLayout";
import { Table } from "../../../../../components/Table/Table";
import { SearchBar } from "../../../../../components/SearchBar/SearchBar";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { useDebounce } from "../../../../../util/hooks";
import { TablePlaceholder, useStoreState } from "../../../../../util/util";
import { endpoints } from "../../../../../endpoints";
import type {
  AdvancedFilterCollectionInstance,
  WithPagination,
} from "../../../../../types/types";
import { ConfigureAdvancedFilterForm } from "./ConfigureAdvancedFilterForm";
import {
  SettingsHeader,
  SettingsDescription,
  TextContainer,
  TableSearchWrapper,
} from "./PorfolioControlsTab";
import { ScrollTableWrapper } from "../../../../SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { TableContainer } from "../../../../SharedPages/ProductDetailPage/Assets/util";

const IconGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_2xl")};
  align-items: center;
`;

const SettingsTitle = styled(TextXlBold)`
  display: block;
`;

const CollectionsSubtitle = styled(TextMdBold)`
  display: block;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_sm")};
`;

/**
 * A mapping's `is_valid` is computed read-time — false once
 * any attribute it references (Key Attribute or a quantitative/other
 * mapping) has been archived/removed. Per spec: disabled, not hidden.
 */
const InvalidRowContent = styled.span`
  color: ${({ theme }) => theme.resolve("color_text_tertiary")};
`;

/** `EditIcon`/`DeleteIcon` are outline icons — pass through `OutlineIconButtonMedium` (see its own doc comment) rather than plain `IconButtonMedium`. */
const DeleteAdvancedFilterButton = (
  props: ComponentProps<typeof OutlineIconButtonMedium>
) => (
  <OutlineIconButtonMedium {...props}>
    <DeleteIcon />
  </OutlineIconButtonMedium>
);

export const AdvancedFiltersMappingsSettings = () => {
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useContext(Notifications);
  const { storefront_id } = useStoreState();
  const [query, setQuery] = useQueryParams({ q: StringParam });
  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [showSlideOut, setShowSlideOut] = useState(false);
  const [editingInstance, setEditingInstance] =
    useState<AdvancedFilterCollectionInstance>();

  const {
    data: instancesData,
    error: instancesError,
    mutate: mutateInstances,
  } = useSWR<WithPagination<{ data: AdvancedFilterCollectionInstance[] }>>([
    endpoints.v2_storefronts_id_advancedFilters_collectionInstances(
      storefront_id
    ),
    useMemo(() => ({ params: { limit: 100, valid_only: false } }), []),
  ]);

  const filteredInstances = useMemo(() => {
    const rows = instancesData?.data ?? [];
    const query = debouncedSearchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      (row) =>
        row.collection_name.toLowerCase().includes(query) ||
        row.key_attribute_display_name.toLowerCase().includes(query)
    );
  }, [instancesData, debouncedSearchQuery]);

  useEffect(() => {
    setQuery({ q: debouncedSearchQuery ? debouncedSearchQuery : undefined });
  }, [setQuery, debouncedSearchQuery]);

  const editInstance = (instance: AdvancedFilterCollectionInstance) => {
    setEditingInstance(instance);
    setShowSlideOut(true);
  };

  const deleteInstance = async (instance: AdvancedFilterCollectionInstance) => {
    try {
      await Axios.delete(
        endpoints.v2_storefronts_id_advancedFilters_collectionInstances_id(
          storefront_id,
          instance.id
        )
      );
      mutateInstances();
      notifySuccess(t("Advanced filter deleted successfully"));
    } catch (error) {
      const errorMessage = (error as AxiosError<{ message?: string }>)?.response
        ?.data?.message;
      notifyError(
        errorMessage ?? t("There was an error deleting this advanced filter"),
        {
          error,
        }
      );
    }
  };

  const closeSlideOut = () => {
    mutateInstances();
    setShowSlideOut(false);
    setEditingInstance(undefined);
  };

  const tableColumns = useMemo(
    () => [
      {
        header: t("Collection"),
        cell: ({
          row: { original },
        }: {
          row: { original: AdvancedFilterCollectionInstance };
        }) =>
          original.is_valid ? (
            <>{original.collection_name}</>
          ) : (
            <InvalidRowContent>{original.collection_name}</InvalidRowContent>
          ),
      },
      {
        header: t("Key Attribute"),
        cell: ({
          row: { original },
        }: {
          row: { original: AdvancedFilterCollectionInstance };
        }) =>
          original.is_valid ? (
            <>{original.key_attribute_display_name}</>
          ) : (
            <InvalidRowContent>
              {original.key_attribute_display_name}
            </InvalidRowContent>
          ),
      },
      {
        header: t("Quantitative Filters"),
        cell: ({
          row: { original },
        }: {
          row: { original: AdvancedFilterCollectionInstance };
        }) => <>{original.quantitative_filters.length}</>,
      },
      {
        header: "",
        accessorKey: " ",
        overflow: "visible",
        cell: ({
          row: { original },
        }: {
          row: { original: AdvancedFilterCollectionInstance };
        }) => (
          <IconGroup style={{ justifyContent: "flex-end" }}>
            <OutlineIconButtonMedium
              testid={`edit-advanced-filter-${original.id}`}
              onClick={() => editInstance(original)}
            >
              <EditIcon />
            </OutlineIconButtonMedium>
            <ButtonWithConfirmDialog
              testid={`delete-advanced-filter-${original.id}`}
              Button={DeleteAdvancedFilterButton}
              handleConfirm={() => deleteInstance(original)}
              confirmMessage={t("Are you sure you want to remove this item?")}
              useV2Buttons
            />
          </IconGroup>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, storefront_id]
  );

  return (
    <BorderCard>
      <SettingsHeader>
        <TextContainer>
          <SettingsTitle>{t("Advanced Filters Mappings")}</SettingsTitle>
          <SettingsDescription>
            {t(
              "Add collections & attribute mappings to the Advanced Filters Modal"
            )}
          </SettingsDescription>
        </TextContainer>
      </SettingsHeader>
      <CollectionsSubtitle>{t("Collections")}</CollectionsSubtitle>
      <TableSearchWrapper>
        <Row>
          <SearchBar
            query={searchQuery}
            placeHolder={t("Search by collection or key attribute")}
            handleChange={(e) => setSearchQuery(e.target.value)}
            handleClearInput={() => {
              setSearchQuery("");
              setQuery({ q: undefined });
            }}
            maxWidth="450px"
          />
          <PrimaryButtonWithPlusIcon onClick={() => setShowSlideOut(true)}>
            {t("Add")}
          </PrimaryButtonWithPlusIcon>
        </Row>
      </TableSearchWrapper>
      <TableContainer>
        <ScrollTableWrapper
          maxHeight="500px"
          style={{ scrollbarWidth: "thin" }}
        >
          <Table
            columns={tableColumns}
            data={filteredInstances}
            isLoading={!instancesError && !instancesData}
            error={undefined}
            Placeholder={<TablePlaceholder message={t("No items to show.")} />}
          />
        </ScrollTableWrapper>
      </TableContainer>
      <SlideOut
        title={
          editingInstance
            ? t("Edit Advanced Filter")
            : t("Configure Advanced Filter")
        }
        closeFlyout={closeSlideOut}
        show={showSlideOut}
      >
        <ConfigureAdvancedFilterForm
          existingInstance={editingInstance}
          handleFormSubmit={closeSlideOut}
        />
      </SlideOut>
    </BorderCard>
  );
};
